import { useEffect, useState } from 'react';
import { Navigate, Outlet, useLocation, useMatches, useNavigate, useSearchParams } from 'react-router-dom';

import { useEmbedBootstrap } from '../use-widget-bootstrap';
import { getMeOnboarding, onboardingPending } from '../widget-api/auth-onboarding';
import { useQuerySuffix } from '../widget-hooks/useQuerySuffix';
import { isAnonymousEmbedBrowsePath, normalizeClientPath } from '../widget-lib/embed-client-path';
import { requiredClientModuleByPath, requiredTrainerModuleByPath } from '../widget-lib/embed-client-module-gates';
import { isTrainerStaffMe, isTrainerSurfaceMe, needsTrainerStudioSelection } from '../widget-lib/embed-route-policy';
import { clearEmbedSession, clearGlobalEmbedSession } from '../widget-lib/embed-session-store';
import { initPushRegistration, registerPushTokenIfPresent } from '../widget-lib/push-token-register';
import { PATH } from '../widget-lib/path-routes';

export type EmbedRouteHandle = {
  /** Страницы без JWT и без key/kid+sig виджета (вход, регистрация, legal, 404). */
  embedPublic?: boolean;
};

function GuardLoading() {
  return (
    <div className="rounded-sm bg-[var(--bg-elevated)] p-6 shadow-sm">
      <p className="text-sm text-[var(--text-muted)]">Загрузка профиля…</p>
    </div>
  );
}

type SessionLostTarget = 'clientHome' | 'trainerSignIn';

/** Сессия недействительна (/auth/me не удался): сброс JWT в localStorage и переход на главную или вход тренера. */
function SessionLostRedirect({ orgSlug, target }: { orgSlug: string | null; target: SessionLostTarget }) {
  const navigate = useNavigate();
  const suffix = useQuerySuffix();

  useEffect(() => {
    if (orgSlug) {
      clearEmbedSession(orgSlug);
    } else {
      clearGlobalEmbedSession();
    }
    const to = target === 'trainerSignIn' ? PATH.trainerSignIn : PATH.home;
    navigate(`${to}${suffix}`, { replace: true });
  }, [navigate, orgSlug, suffix, target]);

  return (
    <div className="rounded-sm bg-[var(--bg-elevated)] p-6 shadow-sm">
      <p className="text-sm text-[var(--text-muted)]">
        {target === 'trainerSignIn'
          ? 'Сессия устарела. Перенаправляем на вход…'
          : 'Сессия устарела. Перенаправляем на главную…'}
      </p>
    </div>
  );
}

/** Клиентские экраны: при подтверждённой роли тренера на сервере — в кабинет тренера. */
export function ClientModeOutlet() {
  const { accessToken, me, meLoading, meError, orgSlug, effectiveModules } = useEmbedBootstrap();
  const suffix = useQuerySuffix();

  if (!accessToken) {
    return <Outlet />;
  }
  if (meLoading) {
    return <GuardLoading />;
  }
  if (meError) {
    return <SessionLostRedirect orgSlug={orgSlug} target="clientHome" />;
  }
  if (isTrainerStaffMe(me)) {
    // Редирект в кабинет тренера только если у организации включен модуль fitness (основной для тренера).
    // Иначе остаёмся на клиентской главной, чтобы не попасть в бесконечный цикл редиректов.
    const mods = effectiveModules ?? me?.modules;
    if (mods?.fitness !== false) {
      return <Navigate to={`${PATH.trainer}${suffix}`} replace />;
    }
  }
  return <Outlet />;
}

/** Маршруты, пропускаемые гейтами онбординга/email (вход, регистрация, legal, 404, сами гейты). */
function isOnboardingBypassPath(normalizedPath: string): boolean {
  return (
    normalizedPath === PATH.signIn ||
    normalizedPath === PATH.register ||
    normalizedPath === PATH.forgotPassword ||
    normalizedPath === PATH.resetPassword ||
    normalizedPath === PATH.agreements ||
    normalizedPath === PATH.confirmEmail ||
    normalizedPath.startsWith('/legal/')
  );
}

/**
 * Клиентские маршруты: без сессии и без параметров виджета в URL — только `handle.embedPublic`.
 * С `canFetchWidget` без JWT — узкий просмотр (расписание, филиалы, карточка слота); остальное — после входа.
 *
 * При наличии JWT дополнительно проверяет:
 * 1. Завершённость онбординга (соглашения, профиль) — редирект на `/agreements`.
 * 2. Подтверждение email — редирект на `/confirm-email`.
 */
export function ClientProtectedOutlet() {
  const location = useLocation();
  const suffix = useQuerySuffix();
  const matches = useMatches();
  const [searchParams] = useSearchParams();
  const { accessToken, canFetchWidget, guestShowcaseBrowse, me, meLoading, effectiveModules, organizationId, orgSlug } =
    useEmbedBootstrap();

  const [onboardingChecked, setOnboardingChecked] = useState(false);
  const [onboardingNeedGate, setOnboardingNeedGate] = useState(false);

  const embedPublic = matches.some(
    (m) => (m.handle as EmbedRouteHandle | undefined)?.embedPublic === true
  );

  const normalizedPath = normalizeClientPath(location.pathname);
  const isBypass = isOnboardingBypassPath(normalizedPath);

  // Регистрация push-токена при старте/восстановлении сессии (не только после свежего логина).
  useEffect(() => {
    if (!accessToken) return;
    void initPushRegistration();
    void registerPushTokenIfPresent();
  }, [accessToken]);

  useEffect(() => {
    if (!accessToken || isBypass) {
      setOnboardingChecked(true);
      setOnboardingNeedGate(false);
      return;
    }
    let cancelled = false;
    setOnboardingChecked(false);
    (async () => {
      try {
        const ob = await getMeOnboarding(organizationId, accessToken);
        if (!cancelled) {
          setOnboardingNeedGate(onboardingPending(ob));
          setOnboardingChecked(true);
        }
      } catch {
        if (!cancelled) {
          setOnboardingNeedGate(false);
          setOnboardingChecked(true);
        }
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [accessToken, organizationId, isBypass]);

  if (embedPublic) {
    return <Outlet />;
  }
  if (accessToken) {
    if (!isBypass) {
      if (meLoading || !onboardingChecked) {
        return (
          <div className="rounded-sm bg-[var(--bg-elevated)] p-6 shadow-sm">
            <p className="text-sm text-[var(--text-muted)]">Проверяем данные…</p>
          </div>
        );
      }

      const hasClientMembership =
        me != null &&
        organizationId != null &&
        (me.memberships ?? []).some(
          (m) => m.organization_id === organizationId && String(m.role).toLowerCase() === 'client'
        );
      if (organizationId != null && !hasClientMembership) {
        return <Navigate to={`${PATH.agreements}${suffix}`} replace />;
      }

      if (onboardingNeedGate) {
        return <Navigate to={`${PATH.agreements}${suffix}`} replace />;
      }

      if (me && !me.email_verified_at && me.email_verification_available) {
        return <Navigate to={`${PATH.confirmEmail}${suffix}`} replace />;
      }
    }

    const requiredModule = requiredClientModuleByPath(normalizedPath);
    const mods = effectiveModules ?? me?.modules;
    if (requiredModule && mods && mods[requiredModule] === false) {
      return <Navigate to={`${PATH.home}${suffix}`} replace />;
    }
    return <Outlet />;
  }
  if (canFetchWidget || guestShowcaseBrowse) {
    const p = normalizeClientPath(location.pathname);
    if (isAnonymousEmbedBrowsePath(p)) {
      return <Outlet />;
    }
  }

  // Глобальный виджет без org контекста — показываем каталог, не редиректим на sign-in.
  const embedGlobal = searchParams.get('embed_global') === '1';
  if (embedGlobal && !orgSlug && !searchParams.get('guest_org_id')) {
    return <Outlet />;
  }

  return (
    <Navigate
      to={`${PATH.signIn}${suffix}`}
      replace
      state={{ from: `${location.pathname}${location.search}` }}
    />
  );
}

/** Маршруты тренера: доступ по контексту токена (`surface=trainer`), не по одной роли. */
export function TrainerProtectedOutlet() {
  const location = useLocation();
  const { accessToken, me, meLoading, meError, orgSlug, effectiveModules } = useEmbedBootstrap();
  const suffix = useQuerySuffix();

  if (!accessToken) {
    return <Navigate to={`${PATH.trainerSignIn}${suffix}`} replace />;
  }
  if (meLoading) {
    return <GuardLoading />;
  }
  if (meError) {
    return <SessionLostRedirect orgSlug={orgSlug} target="trainerSignIn" />;
  }
  if (!isTrainerSurfaceMe(me)) {
    return <Navigate to={`${PATH.home}${suffix}`} replace />;
  }
  if (needsTrainerStudioSelection(me)) {
    return <Navigate to={`${PATH.trainerSelectStudio}${suffix}`} replace />;
  }
  // auth-methods-uniform (правила 5/6): вход в тренерский режим только после подтверждения email.
  if (me && !me.email_verified_at && me.email_verification_available) {
    return <Navigate to={`${PATH.trainer}/confirm-email${suffix}`} replace />;
  }
  const requiredModule = requiredTrainerModuleByPath(normalizeClientPath(location.pathname));
  const mods = effectiveModules ?? me?.modules;
  if (requiredModule && mods && mods[requiredModule] === false) {
    return <Navigate to={`${PATH.home}${suffix}`} replace />;
  }
  return <Outlet />;
}
